import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Logo } from "../../ui/Logo";
import {
  closeConseillerChat,
  consumeConseillerSeed,
  useConseillerChatOpen,
} from "../../lib/conseillerChat";
import { isConseillerOnline, SUPPORT_HOURS_SHORT } from "../../lib/supportHours";
import {
  useAttachments,
  AttachButton,
  PendingChips,
  MsgAttachments,
} from "./attachments";

const CONSEILLER = "Kévin";

const QUESTIONS = [
  "Comment créer un agent ?",
  "Comment diffuser un planning ?",
  "Une question sur ma facture",
  "J'ai un souci technique",
];

export function ConseillerChatWidget() {
  const open = useConseillerChatOpen();
  return (
    <AnimatePresence>{open ? <Panel key="chat" /> : null}</AnimatePresence>
  );
}

function Dots() {
  return (
    <span className="inline-flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-navy-300"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </span>
  );
}

function Panel() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [text, setText] = useState(() => consumeConseillerSeed());
  const scrollRef = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ["support-tickets"],
    queryFn: () => api.support.list(),
  });
  useEffect(() => {
    if (activeId || !list.data) return;
    const ongoing = list.data.find((t) => t.statut !== "ferme");
    if (ongoing) setActiveId(ongoing.id);
  }, [list.data, activeId]);

  const detail = useQuery({
    queryKey: ["support-ticket", activeId],
    queryFn: () => api.support.get(activeId!),
    enabled: !!activeId,
    refetchInterval: 3500,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["support-ticket", activeId] });
    qc.invalidateQueries({ queryKey: ["support-tickets"] });
  };

  const att = useAttachments();
  const send = useMutation({
    mutationFn: async (body: string) => {
      if (activeId) return api.support.message(activeId, body, att.pending);
      const created = await api.support.create({
        sujet: body.slice(0, 120),
        categorie: "Conseiller",
        message: body,
        attachments: att.pending,
      });
      setActiveId(created.id);
      return created;
    },
    onSuccess: () => {
      setText("");
      att.clear();
      invalidate();
    },
  });

  const closeConv = useMutation({
    mutationFn: () => api.support.close(activeId!),
    onSuccess: invalidate,
  });
  const csat = useMutation({
    mutationFn: (s: 0 | 1) => api.support.csat(activeId!, s),
    onSuccess: invalidate,
  });

  const messages = detail.data?.messages ?? [];
  const closed = detail.data?.statut === "ferme";
  const csatScore = detail.data?.csatScore ?? null;
  const typing = detail.data?.conseillerTyping ?? false;
  const online = isConseillerOnline();

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length, send.isPending, typing]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = text.trim();
    if (v.length >= 1 && !send.isPending) send.mutate(v);
  };

  const showQuestions = messages.length === 0 && !send.isPending;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.97 }}
      transition={{ duration: 0.18 }}
      className="fixed bottom-5 right-5 z-50 flex h-[min(580px,82vh)] w-[min(380px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl shadow-navy-800/25 ring-1 ring-navy-100"
    >
      {/* En-tête : logo Victor + conseiller */}
      <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-navy-800 to-accent-700 px-4 py-3.5 text-white">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white shadow-sm">
            <Logo size={22} />
          </span>
          <div>
            <p className="flex items-center gap-1.5 text-sm font-bold leading-tight">
              {CONSEILLER}
              {online ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> en ligne
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/40" /> hors ligne
                </span>
              )}
            </p>
            <p className="text-[11px] text-white/70">
              Conseiller Victor · {SUPPORT_HOURS_SHORT}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={closeConseillerChat}
          aria-label="Fermer"
          className="flex h-8 w-8 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Fil */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-navy-50/40 p-4">
        <div className="rounded-2xl rounded-tl-sm bg-white p-3 text-sm text-navy-700 ring-1 ring-navy-100">
          Bonjour 👋 Expliquez votre besoin, {CONSEILLER} vous répond ici même.
          Vous retrouvez l'échange dans « Mes demandes ».
        </div>
        {!online ? (
          <div className="rounded-2xl bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
            Nous sommes hors ligne ({SUPPORT_HOURS_SHORT}). Laissez votre
            message&nbsp;: nous revenons vers vous dès l'ouverture.
          </div>
        ) : null}

        {showQuestions ? (
          <div className="flex flex-wrap gap-2">
            {QUESTIONS.map((qst) => (
              <button
                key={qst}
                type="button"
                onClick={() => send.mutate(qst)}
                className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-navy-700 ring-1 ring-navy-100 transition-colors hover:bg-navy-50 hover:text-navy-900"
              >
                {qst}
              </button>
            ))}
          </div>
        ) : null}

        {messages.map((m) => {
          const mine = m.authorType === "tenant";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                  mine
                    ? "rounded-tr-sm bg-accent-500 text-white"
                    : "rounded-tl-sm bg-white text-navy-800 ring-1 ring-navy-100"
                }`}
              >
                {!mine ? (
                  <p className="mb-0.5 text-[11px] font-semibold text-navy-400">
                    {m.authorType === "hubblot" ? CONSEILLER : "Assistant"}
                  </p>
                ) : null}
                <p className="whitespace-pre-wrap">{m.body}</p>
                <MsgAttachments items={m.attachments} mine={mine} />
              </div>
            </div>
          );
        })}

        {send.isPending ? (
          <div className="flex justify-end">
            <div className="rounded-2xl rounded-tr-sm bg-accent-500/60 px-3.5 py-2.5 text-sm text-white">
              Envoi…
            </div>
          </div>
        ) : null}

        {/* Conseiller en train d'écrire */}
        {typing ? (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-tl-sm bg-white px-4 py-3 ring-1 ring-navy-100">
              <Dots />
            </div>
          </div>
        ) : null}
      </div>

      {/* Composer / état clôturé */}
      {closed ? (
        <div className="space-y-3 border-t border-navy-100 p-3 text-center">
          {csatScore === null ? (
            <div>
              <p className="text-xs font-semibold text-navy-700">
                Satisfait de l'échange ?
              </p>
              <div className="mt-2 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => csat.mutate(1)}
                  disabled={csat.isPending}
                  className="rounded-full px-3 py-1.5 text-sm ring-1 ring-navy-200 transition-colors hover:bg-emerald-50"
                >
                  👍
                </button>
                <button
                  type="button"
                  onClick={() => csat.mutate(0)}
                  disabled={csat.isPending}
                  className="rounded-full px-3 py-1.5 text-sm ring-1 ring-navy-200 transition-colors hover:bg-amber-50"
                >
                  👎
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-navy-500">Merci pour votre retour 🙏</p>
          )}
          <button
            type="button"
            onClick={() => {
              setActiveId(null);
              setText("");
            }}
            className="text-sm font-semibold text-accent-600 hover:underline"
          >
            Démarrer une nouvelle conversation
          </button>
        </div>
      ) : (
        <div className="border-t border-navy-100 p-3">
          <PendingChips items={att.pending} onRemove={att.remove} />
          <form onSubmit={submit} className="flex items-center gap-2">
            <AttachButton onFile={att.add} busy={att.busy} />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Votre message…"
              autoFocus
              className="flex-1 rounded-full border-0 bg-navy-50 px-4 py-2.5 text-sm text-navy-800 ring-1 ring-navy-100 placeholder:text-navy-400 focus:bg-white focus:ring-2 focus:ring-accent-300 focus:outline-none"
            />
            <button
              type="submit"
              disabled={text.trim().length < 1 || send.isPending}
              aria-label="Envoyer"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 text-white transition-colors hover:bg-accent-600 disabled:opacity-60"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
            </button>
          </form>
          {activeId ? (
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={() => closeConv.mutate()}
                disabled={closeConv.isPending}
                className="text-xs font-medium text-navy-400 hover:text-navy-700"
              >
                Clôturer la conversation
              </button>
            </div>
          ) : null}
        </div>
      )}
    </motion.div>
  );
}
